import axios from 'axios';
import { captureException, captureMessage } from '@sentry/browser';
import { Preferences } from '@capacitor/preferences'

const subscriptionProducts = [
  'com.getmoone.app.subscription.yearly',
  'com.getmoone.app.subscription.monthly'
]

let appStore = null
// Add a Set to track processed transaction IDs
const processedTransactions = new Set();

export const state = {
  products: [],
  ready: false,
  purchaseLoading: false,
  restoreLoading: false,
  purchaseErrorMessage: null,
  purchaseSuccessMessage: null,
  subscriptionStatus: null,
  subscriptionCheckDate: null,
  subscriptionStatusLoaded: false,
  subscriptionCheckDateLoaded: false
}

export const getters = {
  appStore: () => appStore,
  loadedProducts: state => state.products.filter(product => product.loaded),
  formattedProducts: state => state.products.map(product => {
    // Default pricing info
    let price = '';
    let standard_price = '';
    let currency = '';
    
    // Check for subscription offers with pricing phases
    if (product.offers && product.offers.length > 0) {
      const phases = product.offers[0].pricingPhases;
      
      // Check if there's a free trial phase
      const freeTrialPhase = phases.find(
        (phase) => phase.paymentMode === "FreeTrial"
      );
      
      // Find the regular payment phase
      const regularPhase = phases.find(
        (phase) => phase.paymentMode === "PayAsYouGo" || phase.paymentMode === "PayUpFront"
      );
      
      // Use regular phase price for standard_price if available
      if (regularPhase) {
        standard_price = regularPhase.price;
        price = regularPhase.priceMicros;
        currency = regularPhase.currency
      }
    }
    
    return {
      productId: product.id,
      title: product.title || '',
      description: product.description || '',
      price: price,
      standard_price: standard_price,
      currency: currency,
    };
  }),
  isSubscriptionActive: state => {
    if (!state.subscriptionStatusLoaded) return null;
    return state.subscriptionStatus && state.subscriptionStatus.active;
  },
  subscriptionDetails: state => state.subscriptionStatus,
  subscriptionCheckDate: state => state.subscriptionCheckDate,
  isSubscriptionDataLoaded: state => state.subscriptionStatusLoaded
}

export const mutations = {
  START_PURCHASE_LOADING(state) {
    state.purchaseLoading = true;
  },
  STOP_PURCHASE_LOADING(state) {
    state.purchaseLoading = false;
  },
  START_RESTORE_LOADING(state) {
    state.restoreLoading = true;
  },
  STOP_RESTORE_LOADING(state) {
    state.restoreLoading = false;
  },
  ADD_OR_UPDATE_PRODUCT(state, product) {
    // Only add products that have valid data
    if (!product || !product.id) {
      return;
    }
    
    state.products = state.products.filter(val => val.id !== product.id);
    state.products.push({...product, loaded: true});
  },
  SET_PURCHASE_SUCCESS_MESSAGE(state, message) {
    state.purchaseLoading = false;
    state.purchaseErrorMessage = null;
    state.purchaseSuccessMessage = message;
  },
  SET_PURCHASE_ERROR_MESSAGE(state, message) {
    state.purchaseLoading = false;
    state.purchaseErrorMessage = message;
    state.purchaseSuccessMessage = null;
  },
  ACK_PURCHASE(state) {
    state.purchaseErrorMessage = null;
    state.purchaseSuccessMessage = null;
  },
  SET_READY(state, isReady) {
    state.ready = isReady;
  },
  async SET_SUBSCRIPTION_STATUS(state, status) {
    state.subscriptionStatus = status;
    await setPreference('subscriptionStatus', status);
  },
  async SET_SUBSCRIPTION_CHECK_DATE(state, date) {
    state.subscriptionCheckDate = date;
    await setPreference('subscriptionCheckDate', date)
  }
}

// Initialize preferences immediately
const initializePreferences = async () => {
  try {
    const subscriptionStatus = await getPreference('subscriptionStatus');
    if (subscriptionStatus) {
      state.subscriptionStatus = subscriptionStatus;
      state.subscriptionStatusLoaded = true;
    }
  } catch (error) {
    captureException(error)
  }
  
  try {
    const subscriptionCheckDate = await getPreference('subscriptionCheckDate');
    if (subscriptionCheckDate) {
      state.subscriptionCheckDate = subscriptionCheckDate;
      state.subscriptionCheckDateLoaded = true;
    }
  } catch (error) {
    captureException(error)
  }
};

// Call immediately when module loads
initializePreferences();

export const actions = {
  async initialize({ commit }) {
    if (state.ready) {
      return;
    }
    
    document.addEventListener('deviceready', () => {
      appStore = CdvPurchase.store;
      
      if (!appStore) {
        console.error('[Purchases] Store not available');
        captureMessage('Store not available')
        return;
      }
      
      registerProducts(commit);
      
      appStore.error(error => {
        if (error.code == 6777006){
          commit('STOP_PURCHASE_LOADING');
          return
        }
        console.error('[Purchases] Store error:', error);
        captureException(error)
      });      

      appStore.initialize([{
        platform: CdvPurchase.Platform.APPLE_APPSTORE,
        options: {
          needAppReceipt: true
        }
      }]);
      
      commit('SET_READY', true);
    });
  },
  
  async restorePurchases({ commit }) {
    if (!appStore) {
      console.error('[Purchases] Cannot restore: store not initialized');
      return;
    }
    commit('START_RESTORE_LOADING');
    try {
      const response = await axios.get('/subscription/status');
      await commit('SET_SUBSCRIPTION_STATUS', response.data);
      // Check if no subscription was found
      if (response.data.message === 'No subscription found') {
        commit('SET_PURCHASE_SUCCESS_MESSAGE', 'noSubscriptionToRestore');
      } else if (response.data.active == false && response.data.status === 'expired') {
        commit('SET_PURCHASE_SUCCESS_MESSAGE', 'expiredSubscriptionFound');
      } else {
        commit('SET_PURCHASE_SUCCESS_MESSAGE', 'Restore completed, if a purchase was found it was synced');
      }
      
      commit('STOP_RESTORE_LOADING');
      return response.data;
    } catch (error) {
      console.error('[Purchases] Failed to restore purchases:', error);
      captureException(error)
      await commit('SET_SUBSCRIPTION_STATUS', { active: false, error: error.message });
      commit('SET_PURCHASE_ERROR_MESSAGE', 'Failed to restore purchases');
      commit('STOP_RESTORE_LOADING');
      return { active: false, error: error.message };
    }
  },
  
  purchaseProduct({ commit }, productId) {
    if (!appStore) {
      console.error('[Purchases] Cannot purchase: store not initialized');
      captureMessage('[Purchases] Cannot purchase: store not initialized')
      return;
    }
    
    commit('START_PURCHASE_LOADING');
    const product = appStore.get(productId);
    
    if (!product) {
      console.error('[Purchases] Product not found:', productId);
      captureMessage('[Purchases] Product not found: ' . productId)
      return;
    }
    
    const offer = product.getOffer();
    if (!offer) {
      console.error('[Purchases] No offer available for product:', productId);
      captureMessage('[Purchases] No offer available for product: ' . productId)
      return;
    }
    
    offer.order()
      .then(() => {
      })
      .catch(error => {
        console.error('[Purchases] Order failed:', error);
        captureException(error)
      });
  },
  
  async checkSubscriptionStatus({ commit }) {
    try {
      const response = await axios.get('/subscription/status');
      await commit('SET_SUBSCRIPTION_STATUS', response.data);
      return response.data;
    } catch (error) {
      captureException(error)
      await commit(types.SET_SUBSCRIPTION_STATUS, { active: false, error: error.message });
      return { active: false, error: error.message };
    }
  },
  
  // Add new action to check subscription with Apple
  async checkSubscriptionWithApple({ commit }) {
    try {
      const response = await axios.get('/subscription/check-with-apple');
      if (response.data.success) {
      await commit('SET_SUBSCRIPTION_STATUS', response.data.subscription);
      await commit('SET_SUBSCRIPTION_CHECK_DATE', Date.now());
      }
      return response.data;
    } catch (error) {
      captureException(error)
      await commit('SET_SUBSCRIPTION_STATUS', { active: false });
      await commit('SET_SUBSCRIPTION_CHECK_DATE', Date.now());
      return { success: false, error: error.message };
    }
  }
}

function registerProducts(commit) {
  subscriptionProducts.forEach(productId => {
    appStore.register({
      id: productId,
      type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
      platform: CdvPurchase.Platform.APPLE_APPSTORE
    });

    appStore.when(productId).updated(product => {
      // Check if the object is actually a product (not a receipt)
      if (product && product.className !== 'Receipt') {
        if (product.id && (product.title || product.description || product.pricing)) {
          commit('ADD_OR_UPDATE_PRODUCT', product);
        }
      }
    });
    
    appStore.when(productId).approved(transaction => {
      transaction.verify();
    });
    
    appStore.when(productId).verified(receipt => {
      receipt.finish();
    });

    appStore.when(productId).finished(transaction => {
      if(transaction.transactionId && transaction.transactionId!='appstore.application'){
        finishPurchase(transaction.transactionId, commit, appStore.rootState);
      }
    });
  });
}

async function finishPurchase(transactionID, commit, rootState) {
  if (processedTransactions.has(transactionID)) {
    return;
  }
  
  processedTransactions.add(transactionID);
  
  try {
    // Check if user is authenticated by checking the auth token
    const token = await getPreference('token');
    
    if (token) {
      // User is authenticated - send to server immediately
      await axios.post('/subscriptions', {
        transaction_id: transactionID
      });
      await axios.get('/subscription/status').then(async response => {
        await commit('SET_SUBSCRIPTION_STATUS', response.data);
        commit('SET_PURCHASE_SUCCESS_MESSAGE', 'Purchase completed successfully');
      });
    } else {
      // User is not authenticated (during onboarding) - store transaction for later
      // We need to dispatch to a different module, so we'll use the store instance
      // For now, store it in preferences directly
      await setPreference('pendingSubscription', {
        transaction_id: transactionID,
        timestamp: Date.now()
      });
      commit('SET_PURCHASE_SUCCESS_MESSAGE', 'Purchase will be completed after login');
    }
  } catch (error) {
    console.error('[Purchases] Failed to complete purchase:', error);
    captureException(error)
    commit('SET_PURCHASE_ERROR_MESSAGE', 'Failed to complete purchase on server');
  }
}

async function setPreference (key, value) {
  return await Preferences.set({ key, value: JSON.stringify(value) });
}

async function getPreference (key) {
  const result = await Preferences.get({ key });
  return result.value ? JSON.parse(result.value) : null;
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}